import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../app/services/hooks/useOrganizationService';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import DefaultModal from '../../../components/default/DefaultModal';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import InputField from '../../../components/InputField';
import SelectTwo from '../../../components/SelectTwo';

interface Preview {
    user: { id: number; name: string; email: string; roles: string[] };
    has_organization: boolean;
    cases: { own: number; monere_open: number };
}

interface Props {
    show: boolean;
    onClose: () => void;
    onConverted: (organizationId: number) => void;
}

const TYPE_OPTIONS = [
    { value: 'independent', label: 'Abogado independiente' },
    { value: 'firm', label: 'Estudio jurídico' }
];

const EMPTY_FORM = { email: '', organization_type: 'independent', organization_name: '' };

/**
 * Monere convierte a un abogado que ya tenía cuenta (antes de la versión SaaS)
 * en abogado independiente o estudio, con el plan gratuito.
 */
const ConvertLawyerModal: React.FC<Props> = ({ show, onClose, onConverted }) => {
    const { previewConvertLawyer, convertLawyer, fetchingAdminAction } = useOrganizationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const [form, setForm] = useState(EMPTY_FORM);
    const [preview, setPreview] = useState<Preview | null>(null);

    useEffect(() => {
        if (!show) {
            setForm(EMPTY_FORM);
            setPreview(null);
            setErrors({});
        }
    }, [show]);

    const handleSearch = () => {
        setPreview(null);
        previewConvertLawyer(form.email.trim(), {
            onSuccess: (response) => setPreview(response.data),
            onError: (response) => setErrors({ email: [response.message] })
        });
    };

    const handleConvert = () => {
        convertLawyer(form, {
            onSuccess: (response) => {
                toast.success(response.message);
                onConverted(response.data.organization.id);
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => setErrors(response.data)
        });
    };

    const canConvert = !!preview && !preview.has_organization;

    return (
        <DefaultModal title="Convertir abogado existente" show={show} handleClose={onClose}
                      actionButton={canConvert ? <ButtonSaveForm callbackSave={handleConvert} loading={fetchingAdminAction} title="Convertir" /> : undefined}>
            <div>
                <p className="small text-muted">
                    Para abogados que ya tenían cuenta en Monere. Queda como administrador de su propia organización con el
                    plan gratuito. Para sumarlo a un estudio que ya existe, usa «Invitar al estudio» en la ficha del estudio.
                </p>
                <div className="d-flex gap-2 align-items-end mb-3">
                    <div className="flex-grow-1">
                        <InputField label="Correo de la cuenta" type="email" value={form.email} required
                                    onChange={(value) => { setForm({ ...form, email: value }); setPreview(null); }}
                                    onFocus={() => onFocusRemove('email')} error={fieldErrorMessage('email')} />
                    </div>
                    <ButtonGeneral onClick={handleSearch} title="Buscar" variant="outline" size="small" icon="fa-search"
                                   loading={fetchingAdminAction && !preview} />
                </div>

                {preview && (
                    <div className="bg-light rounded p-3 mb-3 small" data-testid="convert-preview">
                        <div><strong>{preview.user.name || preview.user.email}</strong> · {preview.user.email}</div>
                        <div className="text-muted">Roles: {preview.user.roles.join(', ') || 'sin rol'}</div>
                        {preview.has_organization ? (
                            <div className="text-danger mt-2">Ya pertenece a una organización: no se puede convertir.</div>
                        ) : (
                            <div className="mt-2">
                                Pasarán a su organización {preview.cases.own} solicitud(es) que él ingresó.
                                {preview.cases.monere_open > 0 && ` Las ${preview.cases.monere_open} en curso que Monere le asignó vuelven a la bandeja de Monere.`}
                            </div>
                        )}
                    </div>
                )}

                {canConvert && (
                    <>
                        <div className="mb-3">
                            <SelectTwo label="Tipo" inputValue={form.organization_type} options={TYPE_OPTIONS}
                                       onChange={(option: any) => setForm({ ...form, organization_type: option?.value || 'independent' })} />
                        </div>
                        {form.organization_type === 'firm' && (
                            <InputField label="Nombre del estudio" value={form.organization_name} required
                                        onChange={(value) => setForm({ ...form, organization_name: value })}
                                        onFocus={() => onFocusRemove('organization_name')} error={fieldErrorMessage('organization_name')} />
                        )}
                    </>
                )}
            </div>
        </DefaultModal>
    );
};

export default ConvertLawyerModal;
